import { useEffect } from "react";
import PortfolioSlider from "@/components/PortfolioSlider";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lightning, MapTrifold } from "@phosphor-icons/react";

const STATS = [
    { v: "330k+", l: "Drawings shipped" },
    { v: "10+ yrs", l: "On the tools" },
    { v: "48 hrs", l: "Typical turnaround" },
    { v: "50", l: "States covered" },
];

const TRUST = [
    { icon: ShieldCheck, t: "Stamped & recordable", d: "Every plat is QA-reviewed by a licensed PLS before delivery." },
    { icon: Lightning, t: "48-hour turnaround", d: "From upload of survey notes to stamped deliverable. Rush in 12 hrs." },
    { icon: MapTrifold, t: "USA + Canada coverage", d: "Local code compliance for 2,400+ jurisdictions, all 50 states." },
];

export default function Portfolio() {
    useEffect(() => {
        const prev = document.title;
        document.title = "Portfolio · Surveys2CAD — Stamped land-surveying CAD samples";
        return () => { document.title = prev; };
    }, []);
    return (
        <div className="bg-slate-950 text-slate-50">
            {/* Hero */}
            <section className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-12 lg:pt-24">
                <div className="overline mb-3 text-cyan-300">Portfolio · Sample deliverables</div>
                <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5">
                    Stamped CAD drafts <span className="text-cyan-300 neon-text">we&rsquo;ve shipped.</span>
                </h1>
                <p className="text-slate-300 max-w-3xl text-lg leading-relaxed">
                    A curated set of redacted samples from the {STATS[0].v} drawings we&rsquo;ve delivered. Every preview below is a real production
                    file — flipped to view-only so you can review the line-work, callouts, and stamping before requesting a similar drawing.
                </p>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-10 mb-12">
                    {STATS.map((s) => (
                        <div key={s.l} className="tech-card p-5">
                            <div className="overline mb-1">{s.l}</div>
                            <div className="font-heading font-extrabold text-3xl text-slate-50">{s.v}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Slider — expanded variant fills the page */}
            <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20" data-testid="portfolio-main-slider">
                <PortfolioSlider variant="expanded" />
            </section>

            {/* Trust strip */}
            <section className="border-t border-slate-800/80 bg-slate-950/60">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid md:grid-cols-3 gap-6">
                    {TRUST.map(({ icon: Icon, t, d }) => (
                        <div key={t} className="tech-card p-6">
                            <Icon size={28} weight="duotone" className="text-cyan-300 mb-3" />
                            <div className="font-heading font-bold text-lg text-slate-50 mb-2">{t}</div>
                            <p className="text-slate-400 text-sm leading-relaxed">{d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-5xl mx-auto px-5 lg:px-8 py-20 text-center">
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-50 mb-4 tracking-tight">
                    Ready to ship a drawing like these?
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto mb-8 leading-relaxed">
                    Upload your survey notes and we&rsquo;ll quote a fixed price within 1 business hour. First job 20% off.
                </p>
                <Link to="/get-started" className="btn-primary text-base !px-8 !py-3.5" data-testid="portfolio-bottom-cta">
                    Start a project <ArrowRight size={18} weight="bold" />
                </Link>
            </section>
        </div>
    );
}
